import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { SquarePen, X, Eye, AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { CuppingFormSections } from '../../inventory/components/CuppingFormSections';
import { Modal } from '../../../components/common/Modal';
import { AutocompleteSelect, type AutocompleteOption } from '../../../components/common/AutocompleteSelect';
import { roasterService } from '../../../services/roasterService';
import { inventoryService } from '../../../services/inventoryService';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuthStore } from '../../../store/authStore';
import { cuppingSessionService } from '../../../services/cuppingSessionService';
import type { CuppingSession, CreateCuppingSessionPayload } from '../../../types/cuppingSession';
import type { RoastedBulkGroup, RoastedBulkGroupItem } from '../../../types/inventory';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import type { CuppingFormData } from '../../../hooks/inventory-hooks/useCuppingProfileForm';
import { useLanguage } from '../../../features/language/useLanguage';

interface DraftConflictItem {
  inventory_item_id: string;
  item_name: string;
  session_id: string;
  session_code: string;
}

interface CuppingSessionCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (session: CuppingSession) => void;
  existingCount: number;
}

interface RoastedBulkSelection {
  id: string;
  inventoryItemId: string;
  lotId?: string;
  workOrderId?: string;
  workOrderNumber: string;
  itemName: string;
  roastType: string;
  blindCode?: string;
}

type BulkOption = AutocompleteOption & { meta?: Record<string, string | undefined> };
type CuppingMode = 'Simplified' | 'Full' | 'SCA';

const generateBlindCode = () => `${Math.floor(100 + Math.random() * 900)}`;
export const CuppingSessionCreateModal: React.FC<CuppingSessionCreateModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  existingCount,
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { company } = useAuthStore();
  const companyPrefix = (company?.code || 'CS').slice(0, 2).toUpperCase();
  const [cupperId, setCupperId] = useState('');
  const [blindMode, setBlindMode] = useState(true);
  const [cuppingMode, setCuppingMode] = useState<CuppingMode>('Simplified');
  const [selectedItems, setSelectedItems] = useState<RoastedBulkSelection[]>([]);
  const [activeSelection, setActiveSelection] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [previewMode, setPreviewMode] = useState<CuppingMode | null>(null);
  const [draftConflicts, setDraftConflicts] = useState<DraftConflictItem[] | null>(null);

  const previewForm = useForm<CuppingFormData>({
    defaultValues: {
      cuppingType: 'Simplified',
      cuppingDate: new Date().toISOString().split('T')[0],
      roastLevel: 3,
      qcPassed: null,
      defects: [],
      fragrance_aroma: 0,
      fragrance: 0,
      aroma: 0,
      flavor: 0,
      aftertaste: 0,
      acidity: 0,
      sweetness: 0,
      mouthfeel: 0,
      body: 0,
      balance: 0,
      overall: 0,
      notes_fragrance: '',
      notes_flavor: '',
      notes_acidity: '',
      notes_sweetness: '',
      notes_mouthfeel: '',
      notes_body: '',
      fragrance_notes_cata: [],
      flavor_notes_cata: [],
      main_tastes: [],
      mouthfeel_attributes: [],
      acidity_attributes: [],
      roast_defect_underdevelopment: 0,
      roast_defect_overdevelopment: 0,
      roast_defect_baked: 0,
      roast_defect_scorched: 0,
      sca_non_uniform_cups: 0,
      sca_defective_cups: 0,
      sca_final_score: 0,
    }
  });

  const sessionCode = useMemo(() => {
    const next = String(existingCount + 1).padStart(3, '0');
    return `${companyPrefix}CS${next}`;
  }, [companyPrefix, existingCount]);

  const onboardingActive = useOnboardingStore((s) => s.state?.status === 'in_progress');
  // Step 4 is the guided practice cupping — tag the session and signal the
  // overlay to advance once it's created.
  const onboardingStep4 = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 4,
  );
  // The sample cupper seeded for the tour — auto-selected during step 4 so the
  // guided flow only asks the user to pick the two practice roasts.
  const sampleCupperId = useOnboardingStore((s) => s.state?.sampleRefs?.cupperId);
  // Ids of the data seeded for the tour — used to keep the practice cupping off
  // the company's real roasts (see bulkOptions).
  const sampleRefs = useOnboardingStore((s) => s.state?.sampleRefs);

  const { data: roasters = [], isLoading: isLoadingRoasters } = useQuery({
    queryKey: ['roasters', { onboardingActive }],
    queryFn: () => roasterService.getAll({ includeOnboardingSamples: onboardingActive }),
  });

  const { data: roastedBulks = [], isLoading: isLoadingRoastedBulks } = useQuery<RoastedBulkGroup[]>({
    queryKey: ['roasted-bulk-groups', { includeItems: true, onboardingActive }],
    queryFn: () => inventoryService.getRoastedBulkGroups(true, { includeOnboardingSamples: onboardingActive }),
  });

  const { data: allSessions = [] } = useQuery({
    queryKey: ['cupping-sessions', 'all'],
    queryFn: () => cuppingSessionService.getAll(),
  });

  // Inventory items already locked into another draft cupping session.
  // Map preserves the source draft's code for use in the warning Modal.
  const draftLockedItems = useMemo(() => {
    const map = new Map<string, { sessionId: string; sessionCode: string; itemName: string }>();
    for (const session of allSessions) {
      if (session.status !== 'Draft') continue;
      for (const detail of session.details ?? []) {
        if (!detail.inventoryItemId) continue;
        if (!map.has(detail.inventoryItemId)) {
          map.set(detail.inventoryItemId, {
            sessionId: session.id,
            sessionCode: session.code,
            itemName: detail.itemName || '-',
          });
        }
      }
    }
    return map;
  }, [allSessions]);

  const resetForm = () => {
    setCupperId('');
    setBlindMode(true);
    setCuppingMode('Simplified');
    setSelectedItems([]);
    setActiveSelection('');
    setErrorMessage('');
    setEditingId(null);
    setShowConfirmModal(false);
    setDraftConflicts(null);
  };

  const bulkOptions = useMemo<BulkOption[]>(() => {
    // Onboarding step 4 is a PRACTICE cupping: the picker must only ever offer
    // roasts seeded for the tour. Left unfiltered it listed the company's real
    // roasted bulk right next to the samples, so a user could cup live stock —
    // and the tour's cleanup would then delete a session built on real data.
    //
    // Roasted-bulk groups carry no per-row sample flag, so match them the way
    // the practice packing step does: a group is keyed by its SOURCE GREEN BEAN
    // (GetRoastedBulkGroups emits `base.id AS item_id`), so the sample green
    // bean selects both seeded sample roasts AND the roast the user produced in
    // step 3 — every one of them tagged is_onboarding_sample. The explicit
    // roastedBulkId refs are a fallback for state seeded before greenItemId was
    // recorded. Fail open if neither ref exists: a picker with nothing in it
    // would be a hard block.
    const sampleGreenBeanId = sampleRefs?.greenItemId;
    const sampleRoastIds = new Set(
      [sampleRefs?.roastedBulkId1, sampleRefs?.roastedBulkId2].filter(Boolean) as string[],
    );
    const restrictToSamples = onboardingStep4 && (!!sampleGreenBeanId || sampleRoastIds.size > 0);

    return roastedBulks.flatMap((group: RoastedBulkGroup) => {
      const items = group.items || [];
      return items
        .filter((item: RoastedBulkGroupItem) =>
          !restrictToSamples ||
          group.itemId === sampleGreenBeanId ||
          sampleRoastIds.has(item.inventoryItemId),
        )
        .map((item: RoastedBulkGroupItem) => {
          const label = `${item.workOrderNumber} • ${group.itemName} • ${group.roastType.toUpperCase()}`;
          // Import/manual roasts have no work order — their synthetic picker id must map to NULL work_order_id.
          const workOrderId =
            item.workOrderId?.startsWith('manual:') || item.workOrderId?.startsWith('import:')
              ? undefined
              : item.workOrderId;
          return {
            id: item.inventoryItemId,
            value: item.inventoryItemId,
            label,
            text: label,
            meta: {
              inventoryItemId: item.inventoryItemId,
              roastedBulkId: item.inventoryItemId,
              lotId: undefined,
              workOrderId,
              workOrderNumber: item.workOrderNumber,
              itemName: group.itemName,
              roastType: group.roastType,
            },
          } as BulkOption;
        });
    });
  }, [roastedBulks, onboardingStep4, sampleRefs]);

  // The practice cupping is scripted around exactly two roasts (see the comment
  // in availableOptions below). Named so the picker can SAY so rather than
  // silently offering an empty list.
  const practiceRoastLimitReached = onboardingStep4 && selectedItems.length >= 2;

  const availableOptions = useMemo<BulkOption[]>(() => {
    // The practice cupping is scripted around exactly two roasts: the guide's
    // banner says so, and its scoring phase walks tab 0 then tab 1. Restricting
    // the picker to onboarding samples still leaves three (two seeded roasts
    // plus the one the user roasted in step 3), so a third could be added and
    // the tour then stalled on a roast the guide never mentions. Offer nothing
    // more once two are picked — removing one re-opens the list.
    if (practiceRoastLimitReached) return [];
    const selectedIds = new Set(selectedItems.map((item) => item.inventoryItemId));
    return bulkOptions.filter((option) => {
      const id = (option.value || option.id) as string;
      if (selectedIds.has(id)) return false;
      if (draftLockedItems.has(id)) return false;
      return true;
    });
  }, [bulkOptions, selectedItems, draftLockedItems, practiceRoastLimitReached]);

  const cupperOptions = useMemo(
    () =>
      roasters
        .filter((roaster) => roaster.isActive)
        .map((roaster) => ({
          id: roaster.id,
          value: roaster.id,
          label: roaster.name,
          text: roaster.name,
        })) as AutocompleteOption[],
    [roasters]
  );

  // Auto-select the seeded sample cupper during the guided step-4 cupping so the
  // user only has to pick the two practice roasts. Wait until the option is
  // loaded so the AutocompleteSelect can resolve its display label.
  useEffect(() => {
    if (!onboardingStep4 || !sampleCupperId || cupperId) return;
    if (!cupperOptions.some((o) => o.value === sampleCupperId)) return;
    // Intentional one-shot: pre-fill the seeded cupper once its option loads.
    // Guarded by `cupperId` (returns once set), so it runs at most once.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCupperId(sampleCupperId);
  }, [onboardingStep4, sampleCupperId, cupperId, cupperOptions]);

  const createSessionMutation = useMutation({
    mutationFn: (payload: CreateCuppingSessionPayload) => cuppingSessionService.create(payload),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['cupping-sessions'] });
      // Onboarding step 4 advances only after the user scores and completes the
      // cupping on the detail page — not here. onCreated navigates there next.
      resetForm();
      onCreated(session);
    },
    onError: (error: unknown) => {
      // Backend returns 409 + { error: 'cupping_session.item_in_draft', items: [...] }
      // when a roasted bulk is already attached to another draft session.
      if (isAxiosError(error)) {
        const data = error.response?.data as { error?: string; items?: DraftConflictItem[] } | undefined;
        if (data?.error === 'cupping_session.item_in_draft' && Array.isArray(data.items) && data.items.length > 0) {
          setDraftConflicts(data.items);
          // Refresh cached sessions so the dropdown filter immediately reflects the new conflict.
          queryClient.invalidateQueries({ queryKey: ['cupping-sessions'] });
          return;
        }
        const message = (data as { message?: string } | undefined)?.message;
        setErrorMessage(message || t('cuppingSessions.create.errors.unknown'));
        return;
      }
      setErrorMessage(t('cuppingSessions.create.errors.unknown'));
    },
  });
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAddSelection = (_value: string, option?: AutocompleteOption) => {
    const bulkOption = option as BulkOption | undefined;
    if (!bulkOption?.meta) return;
    const meta = bulkOption.meta;
    const optionValue = bulkOption.value || bulkOption.id || '';
    const newItem: RoastedBulkSelection = {
      id: meta.roastedBulkId || optionValue,
      inventoryItemId: meta.inventoryItemId || optionValue,
      lotId: meta.lotId,
      workOrderId: meta.workOrderId,
      workOrderNumber: meta.workOrderNumber || '-',
      itemName: meta.itemName || '-',
      roastType: meta.roastType || '-',
      blindCode: blindMode ? generateBlindCode() : undefined,
    };
    if (!newItem.inventoryItemId) return;
    // Belt-and-braces companion to the availableOptions cap above: keep the
    // practice session at two roasts whichever way the value arrives.
    if (practiceRoastLimitReached) return;
    setSelectedItems((prev) => [...prev, newItem]);
    setActiveSelection('');
    setErrorMessage('');
  };

  const handleRemoveItem = (inventoryItemId: string) => {
    setSelectedItems((prev) => prev.filter((item) => item.inventoryItemId !== inventoryItemId));
  };

  const handleSaveDraftClick = () => {
    if (!cupperId) {
      setErrorMessage(t('cuppingSessions.create.errors.selectCupper'));
      return;
    }
    if (selectedItems.length < 2) {
      setErrorMessage(t('cuppingSessions.create.errors.selectMinItems'));
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    const payload: CreateCuppingSessionPayload = {
      code: sessionCode,
      cupperId,
      blindMode,
      cuppingMode,
      onboardingSample: onboardingStep4,
      details: selectedItems.map((item, index) => ({
        roastedBulkId: item.id,
        inventoryItemId: item.inventoryItemId,
        lotId: item.lotId,
        workOrderId: item.workOrderId,
        workOrderNumber: item.workOrderNumber,
        itemName: item.itemName,
        roastType: item.roastType,
        blindCode: item.blindCode,
        orderIndex: index,
      })),
    };
    createSessionMutation.mutate(payload);
  };

  const gridColumns = selectedItems.length < 5 ? 'grid-cols-1' : selectedItems.length < 10 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <>
    <Modal isOpen={isOpen} onClose={handleClose} title={t('cuppingSessions.create.title')} maxWidth="xl">
      <div className="space-y-6" data-test-id="cupping-session-create-modal">
        {(isLoadingRoasters || isLoadingRoastedBulks) && <LoadingSpinner text={t('cuppingSessions.create.loadingData')} />}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('cuppingSessions.columns.code')}</label>
            <input
              data-test-id="cupping-session-code-input"
              value={sessionCode}
              readOnly
              className="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 text-gray-700 shadow-sm sm:text-sm border p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('cuppingSessions.columns.cupper')}</label>
            <AutocompleteSelect
              dataTestId="cupping-session-cupper-select"
              value={cupperId}
              onChange={(value) => setCupperId(value)}
              options={cupperOptions}
              placeholder={t('cuppingSessions.create.selectCupper')}
              disabled={isLoadingRoasters}
              allowCustom={false}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('cuppingSessions.create.cuppingMode')}</label>
          <div className="flex items-stretch bg-gray-100 rounded-lg p-1 w-full sm:w-fit">
            {(['Simplified', 'Full', 'SCA'] as CuppingMode[]).map((mode) => (
              <div key={mode} className="flex items-stretch flex-1 sm:flex-initial min-w-0">
                <button
                  type="button"
                  data-test-id={`cupping-mode-${mode.toLowerCase()}`}
                  onClick={() => setCuppingMode(mode)}
                  className={`flex-1 sm:flex-initial px-1.5 sm:px-3 py-1.5 text-[11px] sm:text-xs font-bold leading-tight text-center rounded-md transition-all min-w-0 ${
                    cuppingMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {mode === 'Full' ? t('cupping.formSections.mode.descriptive') : mode === 'SCA' ? t('cupping.formSections.mode.affective') : t('cupping.formSections.mode.simplified')}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewMode(mode);
                  }}
                  className="flex items-center justify-center px-1 sm:px-1.5 text-gray-400 hover:text-primary-600 transition-colors shrink-0"
                  title={t('cuppingSessions.create.previewMode', { mode: mode === 'Full' ? t('cupping.formSections.mode.descriptive') : mode === 'SCA' ? t('cupping.formSections.mode.affective') : t('cupping.formSections.mode.simplified') })}
                >
                  <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
                {mode !== 'SCA' && <div className="self-center w-px h-4 bg-gray-300 mx-0.5 sm:mx-1 shrink-0" />}
              </div>
            ))}
          </div>
          <p className="text-xs text-red-400 mt-1">{t('cuppingSessions.create.modeLockedNote')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('cuppingSessions.create.roastedBulkSelection')}</label>
          <div className="mt-1">
            <AutocompleteSelect
              dataTestId="cupping-session-bulk-select"
              value={activeSelection}
              onChange={handleAddSelection}
              options={availableOptions}
              placeholder={t('cuppingSessions.create.searchBulkPlaceholder')}
              // Disabled rather than left open on an empty list: a field that
              // takes focus and then shows nothing reads as broken.
              disabled={isLoadingRoastedBulks || practiceRoastLimitReached}
              allowCustom={false}
            />
          </div>
          <p
            className="text-xs text-gray-500 mt-2"
            data-test-id="cupping-session-bulk-select-note"
          >
            {practiceRoastLimitReached
              ? t(
                  'cuppingSessions.create.practiceRoastLimit',
                  undefined,
                  'The practice cupping compares two roasts. Remove one below to pick a different roast.',
                )
              : t('cuppingSessions.create.allBulksAvailable')}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            data-test-id="cupping-session-blind-mode"
            type="checkbox"
            checked={blindMode}
            onChange={(event) => {
              const value = event.target.checked;
              setBlindMode(value);
              setSelectedItems((prev) =>
                prev.map((item) => ({
                  ...item,
                  blindCode: value ? item.blindCode || generateBlindCode() : undefined,
                }))
              );
            }}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">{t('cuppingSessions.create.enableBlindMode')}</span>
        </div>

        <div className={`grid gap-4 ${gridColumns}`}>
          {selectedItems.map((item) => (
            <div
              key={item.inventoryItemId}
              data-test-id="cupping-selected-item"
              className="border border-gray-200 rounded-lg p-4 space-y-2 bg-white hover:border-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Header Area: Blind Code / Work Order */}
                  <div className="flex items-center gap-2 group">
                    {editingId === item.inventoryItemId ? (
                      // Tampilan saat Mode Edit
                      <input
                        autoFocus
                        className="text-sm font-semibold text-gray-900 border-b border-blue-500 outline-none w-full bg-transparent"
                        value={item.blindCode || ''}
                        onBlur={() => setEditingId(null)} // Selesai edit saat klik luar
                        onKeyDown={(e) => e.key === 'Enter' && setEditingId(null)} // Selesai saat tekan Enter
                        onChange={(event) =>
                          setSelectedItems((prev) =>
                            prev.map((entry) =>
                              entry.inventoryItemId === item.inventoryItemId
                                ? { ...entry, blindCode: event.target.value }
                                : entry
                            )
                          )
                        }
                      />
                    ) : (
                      // Tampilan Default
                      <>
                        <span className="text-sm font-bold text-gray-900">
                          {blindMode ? item.blindCode || t('cuppingSessions.create.noCode') : item.workOrderNumber}
                        </span>
                        {blindMode && (
                          <button 
                            onClick={() => setEditingId(item.inventoryItemId)}
                            className="text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <SquarePen className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Subtitle Area */}
                  <div className="text-xs text-gray-500 mt-1">
                    {blindMode ? t('cuppingSessions.create.blindCode') : item.itemName}
                  </div>
                  {!blindMode && (
                    <div className="text-xs text-gray-400 italic uppercase">{item.roastType}</div>
                  )}
                </div>

                {/* Tombol Remove di pojok kanan */}
                <button
                  onClick={() => handleRemoveItem(item.inventoryItemId)}
                  className="text-red-400 hover:text-red-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {errorMessage && (
          <div className="text-sm text-red-600" data-test-id="cupping-session-create-error">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            data-test-id="cupping-session-cancel"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            {t('auth.common.cancel')}
          </button>
          <button
            data-test-id="cupping-session-save-draft"
            onClick={handleSaveDraftClick}
            disabled={createSessionMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {createSessionMutation.isPending ? t('forms.actions.saving') : t('cuppingSessions.create.saveDraft')}
          </button>
        </div>
      </div>
    </Modal>

      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title={t('cuppingSessions.create.confirmTitle')} maxWidth="sm">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <svg className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('cuppingSessions.create.confirmLock')}</p>
              <p className="text-xs text-amber-700 mt-1">
                {t('cuppingSessions.create.selectedModePrefix')} <strong>{cuppingMode === 'Full' ? t('cupping.formSections.mode.descriptive') : cuppingMode === 'SCA' ? t('cupping.formSections.mode.affective') : t('cupping.formSections.mode.simplified')}</strong> {t('cuppingSessions.create.selectedModeSuffix')}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              data-test-id="cupping-confirm-cancel"
              onClick={() => setShowConfirmModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('auth.common.cancel')}
            </button>
            <button
              data-test-id="cupping-confirm-proceed"
              onClick={handleConfirmSave}
              disabled={createSessionMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {createSessionMutation.isPending ? t('forms.actions.saving') : t('purchaseOrder.form.modals.proceed')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal 
        isOpen={!!previewMode} 
        onClose={() => setPreviewMode(null)} 
        title={`${t('cuppingSessions.create.preview')}: ${previewMode === 'Full' ? t('cupping.formSections.mode.descriptive') : previewMode === 'SCA' ? t('cupping.formSections.mode.affective') : t('cupping.formSections.mode.simplified')}`}
        maxWidth="5xl"
      >
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{t('cuppingSessions.create.formPreviewDisabled')}</h4>
            <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-[10px] font-bold">{t('cuppingSessions.create.readOnly')}</span>
          </div>
          {previewMode && (
            <div className="pointer-events-none opacity-80">
              <CuppingFormSections
                control={previewForm.control}
                register={previewForm.register}
                setValue={previewForm.setValue}
                getValues={previewForm.getValues}
                watch={previewForm.watch}
                maxScore={10}
                mode={previewMode}
                readOnly
              />
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setPreviewMode(null)}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('cuppingSessions.create.closePreview')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!draftConflicts}
        onClose={() => setDraftConflicts(null)}
        title={t('cuppingSessions.create.errors.itemInDraft.title')}
        maxWidth="md"
      >
        <div className="space-y-4" data-test-id="cupping-session-draft-conflict-warning">
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-sm text-red-800">
              {t('cuppingSessions.create.errors.itemInDraft.intro')}
            </p>
          </div>
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {(draftConflicts ?? []).map((item) => (
              <li
                key={`${item.inventory_item_id}-${item.session_id}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-gray-200 bg-gray-50"
              >
                <span className="text-sm font-medium text-gray-900 truncate">
                  {item.item_name || '-'}
                </span>
                <span className="text-xs font-mono px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                  {t('cuppingSessions.create.errors.itemInDraft.inSession', { code: item.session_code })}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            {t('cuppingSessions.create.errors.itemInDraft.hint')}
          </p>
          <div className="flex justify-end">
            <button
              data-test-id="cupping-session-draft-conflict-dismiss"
              onClick={() => setDraftConflicts(null)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('cuppingSessions.create.errors.itemInDraft.dismiss')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
